/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/

/**
 * Centralized service registry to manage dependencies and break circular dependencies
 * Provides dependency injection pattern for better testability and maintainability
 */

export class ServiceRegistry {
  private static services = new Map<string, any>();
  private static initialized = false;

  /**
   * Register a service instance
   * @param name - Service identifier
   * @param service - Service instance
   */
  static register<T>(name: string, service: T): void {
    if (this.services.has(name)) {
      console.warn(`Service "${name}" is already registered. Overwriting...`);
    }
    this.services.set(name, service);
  }

  /**
   * Get a registered service
   * @param name - Service identifier
   * @returns Service instance or throws if not found
   */
  static get<T>(name: string): T {
    const service = this.services.get(name);
    if (!service) {
      throw new Error(`Service "${name}" not found in registry. Make sure it's registered before use.`);
    }
    return service as T;
  }

  /**
   * Check if a service is registered
   * @param name - Service identifier
   * @returns True if service exists
   */
  static has(name: string): boolean {
    return this.services.has(name);
  }

  /**
   * Get all registered service names
   * @returns Array of service names
   */
  static getServiceNames(): string[] {
    return Array.from(this.services.keys());
  }

  /**
   * Clear all services (useful for testing)
   */
  static clear(): void {
    this.services.clear();
    this.initialized = false;
  }

  /**
   * Mark registry as initialized
   */
  static markInitialized(): void {
    this.initialized = true;
  }

  /**
   * Check if registry is initialized
   * @returns True if initialized
   */
  static isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Get service or null if not found (safer alternative)
   * @param name - Service identifier
   * @returns Service instance or null
   */
  static getOrNull<T>(name: string): T | null {
    return this.services.has(name) ? (this.services.get(name) as T) : null;
  }
}
